// Row-body expansion: a genuine click on the row body toggles it; a drag, a text selection or a click on a
// control inside the row does not. Query, page and page-size changes clear it in the reducer, and
// ExpansionWatch clears it when a row is removed.

import { useCallback, useRef } from 'react'

import { useTable } from '../context'
import { ACTIONS } from '../types'

// these targets keep a row-body click from toggling expansion
export const INTERACTIVE = [
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  'audio[controls]',
  'video[controls]',
  'iframe',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex^="-"])',
  ...[
    'button',
    'link',
    'menuitem',
    'menuitemcheckbox',
    'menuitemradio',
    'checkbox',
    'radio',
    'switch',
    'tab',
    'menu',
    'listbox',
    'option',
    'combobox',
    'textbox',
    'searchbox',
    'slider',
    'spinbutton',
    'dialog',
  ].map(role => `[role="${role}"]`),
].join(', ')
export const DRAG_TOLERANCE_PX = 4

export const useExpansion = () => {
  const table = useTable()
  const { state, dispatch, layout } = table
  const pointerStart = useRef(null)
  const { expandable } = layout

  const toggle = useCallback(
    id => dispatch({ type: ACTIONS.rowToggle, id, reason: 'expansion' }),
    [dispatch]
  )

  const onPointerDown = event => {
    pointerStart.current = { x: event.clientX, y: event.clientY }
  }

  const onClick = (event, id) => {
    if (!expandable) return
    const start = pointerStart.current
    pointerStart.current = null
    if (
      start &&
      Math.hypot(event.clientX - start.x, event.clientY - start.y) >
        DRAG_TOLERANCE_PX
    )
      return
    const selection = window.getSelection
      ? String(window.getSelection() || '')
      : ''
    if (selection) return
    const { target, currentTarget } = event
    // a click from a portal (an open menu) reaches the row through React, not through the DOM
    if (!target || !currentTarget.contains(target)) return
    // only a control inside the row counts; a focusable ancestor such as the scroll region does not
    const control = target.closest ? target.closest(INTERACTIVE) : null
    if (control && currentTarget.contains(control)) return
    toggle(id)
  }

  return {
    isOpen: id => state.expanded.has(id),
    toggle,
    onPointerDown,
    onClick,
  }
}
